/**
 * BriefingPacienteCard — Resumen inteligente del paciente generado por IA.
 * Reemplaza el antiguo NaraTerminalCard tipo "consola hacker" por un card
 * clínico profesional, integrado al lenguaje visual del expediente.
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip, Skeleton, Paper, Collapse,
} from '@mui/material';
import {
  AutoAwesome as SparkleIcon,
  Refresh as RefreshIcon,
  KeyboardArrowUp as ChevronUpIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Event as EventIcon,
  WhatsApp as WhatsAppIcon,
  HistoryEdu as HistoryIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';

function tiempoRelativo(iso) {
  if (!iso) return '';
  const ahora = new Date();
  const dt = new Date(iso);
  const diffMs = ahora - dt;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'hace un momento';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'hace 1 día';
  if (d < 30) return `hace ${d} días`;
  return dt.toLocaleDateString('es', { day: '2-digit', month: 'short' });
}

export default function BriefingPacienteCard({
  briefing,
  loading = false,
  onRegenerar,
}) {
  const [expandido, setExpandido] = useState(true);
  const [copiado, setCopiado] = useState(false);

  // Auto-resetear el "copiado" después de 2s
  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 2000);
    return () => clearTimeout(t);
  }, [copiado]);

  if (!loading && !briefing) return null;

  const tieneAlertas = Array.isArray(briefing?.alertas) && briefing.alertas.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2, p: 2,
        borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(238, 242, 255, 0.85) 0%, rgba(245, 243, 255, 0.85) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(165, 180, 252, 0.4)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Stripe de gradiente animado a la izquierda */}
      <Box sx={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
        background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
      }} />

      {/* Header: avatar + título + acciones */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
        }}>
          <SparkleIcon sx={{ color: '#fff', fontSize: 19 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 800, color: '#4338ca',
              textTransform: 'uppercase', letterSpacing: 0.7,
            }}>
              Resumen inteligente
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: '#7c3aed', fontWeight: 600 }}>
              · Generado por NARA
            </Typography>
            {briefing?.generado_en && (
              <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
                · {tiempoRelativo(briefing.generado_en)}
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.1 }}>
            Resumen clínico previo a la consulta
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
          {onRegenerar && (
            <Tooltip title="Regenerar resumen">
              <IconButton
                size="small"
                onClick={onRegenerar}
                disabled={loading}
                sx={{
                  color: '#6366f1',
                  '&:hover': { background: 'rgba(99, 102, 241, 0.1)' },
                }}
              >
                <RefreshIcon sx={{
                  fontSize: 17,
                  ...(loading ? { animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } } : {}),
                }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={expandido ? 'Colapsar' : 'Expandir'}>
            <IconButton
              size="small"
              onClick={() => setExpandido(!expandido)}
              sx={{
                color: '#64748b',
                '&:hover': { background: 'rgba(100, 116, 139, 0.1)' },
              }}
            >
              {expandido ? <ChevronUpIcon sx={{ fontSize: 19 }} /> : <ChevronDownIcon sx={{ fontSize: 19 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={expandido}>
        {/* Cuerpo: narrativa + chips */}
        <Box sx={{ pl: { xs: 0, sm: 6 } }}>
          {loading ? (
            <Box>
              <Skeleton variant="text" width="95%" height={18} sx={{ background: 'rgba(165, 180, 252, 0.25)' }} />
              <Skeleton variant="text" width="88%" height={18} sx={{ background: 'rgba(165, 180, 252, 0.25)' }} />
              <Skeleton variant="text" width="70%" height={18} sx={{ background: 'rgba(165, 180, 252, 0.25)' }} />
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1.2 }}>
                <Skeleton variant="rounded" width={120} height={22} sx={{ background: 'rgba(165, 180, 252, 0.25)' }} />
                <Skeleton variant="rounded" width={140} height={22} sx={{ background: 'rgba(165, 180, 252, 0.25)' }} />
                <Skeleton variant="rounded" width={100} height={22} sx={{ background: 'rgba(165, 180, 252, 0.25)' }} />
              </Box>
            </Box>
          ) : (
            <>
              {/* Texto narrativo */}
              <Typography sx={{
                fontSize: 13.5,
                color: '#1e1b4b',
                lineHeight: 1.65,
                mb: tieneAlertas || briefing?.ultima_consulta || briefing?.proxima_cita || briefing?.ultimo_contacto_whatsapp ? 1.2 : 0,
              }}>
                {briefing?.resumen}
              </Typography>

              {/* Alertas (si las hay) — destacadas en rojo */}
              {tieneAlertas && (
                <Box sx={{
                  mt: 1, p: 0.9, borderRadius: 1.5,
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  display: 'flex', alignItems: 'flex-start', gap: 0.7,
                }}>
                  <WarningIcon sx={{ fontSize: 16, color: '#dc2626', flexShrink: 0, mt: 0.1 }} />
                  <Box>
                    <Typography sx={{
                      fontSize: 9.5, fontWeight: 800, color: '#991b1b',
                      textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.2,
                    }}>
                      Alertas detectadas
                    </Typography>
                    {briefing.alertas.map((a, i) => (
                      <Typography key={i} sx={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.4 }}>
                        • {a}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Chips de datos clave */}
              {(briefing?.ultima_consulta || briefing?.proxima_cita || briefing?.ultimo_contacto_whatsapp) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {briefing?.ultima_consulta && (
                    <Chip
                      icon={<HistoryIcon sx={{ fontSize: 13, color: '#0891b2 !important' }} />}
                      label={briefing.ultima_consulta}
                      size="small"
                      sx={{
                        height: 22, fontSize: 11,
                        background: '#ecfeff', color: '#0c4a6e',
                        border: '1px solid #a5f3fc',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {briefing?.proxima_cita && (
                    <Chip
                      icon={<EventIcon sx={{ fontSize: 13, color: '#7c3aed !important' }} />}
                      label={briefing.proxima_cita}
                      size="small"
                      sx={{
                        height: 22, fontSize: 11,
                        background: '#f5f3ff', color: '#5b21b6',
                        border: '1px solid #ddd6fe',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {briefing?.ultimo_contacto_whatsapp && (
                    <Chip
                      icon={<WhatsAppIcon sx={{ fontSize: 13, color: '#16a34a !important' }} />}
                      label={briefing.ultimo_contacto_whatsapp}
                      size="small"
                      sx={{
                        height: 22, fontSize: 11,
                        background: '#f0fdf4', color: '#14532d',
                        border: '1px solid #86efac',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
